<template>
  <div>
    <DrawerToolbar
      :title="type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Billboard'"
      :admin="true"
      :showBackButton="showBackButton"
      :showCloseButton="!showBackButton"
      @back="handleBack"
      @close="handleBack"
    />

    <v-card-text class="py-6 px-4">
      <!-- Preview -->
      <p class="subtitle-1 font-weight-bold mb-3">Preview</p>
      <Billboard :config="Config" />

      <v-divider class="my-6" />

      <!-- Billboard Size -->
      <v-select
        v-model="aspectRatio"
        :items="aspectRatioItems"
        item-text="text"
        item-value="value"
        label="Billboard Size"
        outlined
        dense
        class="mb-2"
      />

      <!-- Card background -->
      <p class="subtitle-1 font-weight-bold mb-2">Card background</p>

      <!-- Color radio -->
      <div class="d-flex align-center mb-1 radio-option" @click="backgroundMode = 'color'">
        <v-icon :color="backgroundMode === 'color' ? 'brandCyan' : 'grey darken-1'">
          {{ backgroundMode === "color" ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
        </v-icon>
        <span class="ml-2 body-1">Color</span>
      </div>

      <!-- Color fields (indented) -->
      <div v-if="backgroundMode === 'color'" class="pl-10 mb-2">
        <div class="d-flex align-center mb-2">
          <input
            type="color"
            v-model="primaryColor"
            aria-label="Primary background color"
            data-testid="primary-color-input"
            class="color-swatch"
          />
          <v-text-field
            v-model="primaryColor"
            label="Primary color (in hexadecimal)"
            outlined
            dense
          />
        </div>

        <!-- Gradient secondary row -->
        <div v-if="showGradient" class="d-flex align-center mb-2">
          <input
            type="color"
            v-model="secondaryColor"
            aria-label="Secondary background color"
            data-testid="secondary-color-input"
            class="color-swatch"
          />
          <v-text-field
            v-model="secondaryColor"
            label="Gradient secondary color (in hexadecimal)"
            outlined
            dense
          />
          <Button
            icon
            aria-label="Remove gradient"
            data-testid="delete-gradient"
            @click="removeGradient"
          >
            <Icon icon="mdi-delete" />
          </Button>
        </div>

        <!-- Add Gradient button -->
        <Button
          v-if="!showGradient"
          text
          iconLeft="mdi-plus"
          color="brandCyan"
          small
          class="pl-0 mb-2"
          @click="addGradient"
          >ADD GRADIENT</Button
        >
      </div>

      <!-- Banner Image radio -->
      <div class="d-flex align-center mb-1 radio-option" @click="backgroundMode = 'image'">
        <v-icon :color="backgroundMode === 'image' ? 'brandCyan' : 'grey darken-1'">
          {{ backgroundMode === "image" ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
        </v-icon>
        <span class="ml-2 body-1">Banner Image</span>
      </div>

      <!-- Image uploader (indented) -->
      <div v-if="backgroundMode === 'image'" class="pl-10 mb-2">
        <DragAndDropImageInput
          :existingImageUrl="imageUrl"
          :aspectRatioValue="aspectRatioValue"
          :aspectRatioText="aspectRatio"
          crop-image
          @file-selected="onFileSelected"
          @clear-existing-image="onClearImage"
        />
        <v-progress-linear
          v-if="uploading"
          indeterminate
          color="brandCyan"
          aria-label="Uploading image"
          class="mt-2"
        />
        <p
          v-if="uploadError"
          role="alert"
          aria-live="polite"
          class="error--text caption mt-1"
          data-testid="upload-error"
        >
          {{ uploadError }}
        </p>
      </div>

      <v-divider class="my-6" />

      <!-- Title toggle -->
      <div class="d-flex align-center mb-4">
        <v-switch
          v-model="titleEnabled"
          color="brandCyan"
          hide-details
          class="mt-0 pt-0 mr-1 shrink-0"
        />
        <span class="subtitle-1 font-weight-bold">Title</span>
      </div>

      <!-- Title fields -->
      <template v-if="titleEnabled">
        <v-text-field
          data-testid="title-input"
          v-model="title"
          label="Title"
          outlined
          dense
          class="mb-4"
        />

        <!-- Format row: color swatch + B/I/U + alignment + size -->
        <div class="d-flex align-center mb-4">
          <input
            type="color"
            v-model="titleColor"
            aria-label="Title color"
            data-testid="title-color-input"
            class="title-color-swatch mr-3"
          />

          <Button
            icon
            small
            aria-label="Bold"
            :color="titleBold ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleBold }"
            @click="titleBold = !titleBold"
          >
            <Icon icon="mdi-format-bold" />
          </Button>
          <Button
            icon
            small
            aria-label="Italic"
            :color="titleItalic ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleItalic }"
            @click="titleItalic = !titleItalic"
          >
            <Icon icon="mdi-format-italic" />
          </Button>
          <Button
            icon
            small
            aria-label="Underline"
            :color="titleUnderline ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleUnderline }"
            @click="titleUnderline = !titleUnderline"
          >
            <Icon icon="mdi-format-underline" />
          </Button>

          <Button
            icon
            small
            aria-label="Align left"
            :color="horizontalAlignment === 'left' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': horizontalAlignment === 'left' }"
            @click="horizontalAlignment = 'left'"
          >
            <Icon icon="mdi-format-align-left" />
          </Button>
          <Button
            icon
            small
            aria-label="Align center"
            :color="horizontalAlignment === 'center' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': horizontalAlignment === 'center' }"
            @click="horizontalAlignment = 'center'"
          >
            <Icon icon="mdi-format-align-center" />
          </Button>
          <Button
            icon
            small
            aria-label="Align right"
            :color="horizontalAlignment === 'right' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': horizontalAlignment === 'right' }"
            @click="horizontalAlignment = 'right'"
          >
            <Icon icon="mdi-format-align-right" />
          </Button>

          <v-select
            v-model="titleSize"
            :items="titleSizeItems"
            item-text="text"
            item-value="value"
            label="Size"
            outlined
            dense
            hide-details
            class="ml-3"
          />
        </div>

        <!-- Vertical Alignment -->
        <v-select
          v-model="verticalAlignment"
          :items="verticalAlignmentItems"
          item-text="text"
          item-value="value"
          label="Vertical Alignment"
          outlined
          dense
          class="vertical-alignment-select"
        />
      </template>

      <v-divider class="my-6" />

      <!-- Audience targeting (top-section rows only) -->
      <AudienceGroupSelector
        v-if="!row.programId"
        :clientId="clientId || globalClientId"
        :audienceGroups="audienceGroups"
        @update:audienceGroups="audienceGroups = $event"
        data-testid="audience-selector"
      />
    </v-card-text>

    <!-- Save error / progress -->
    <p
      v-if="saveError"
      role="alert"
      aria-live="polite"
      class="error--text caption px-4 pb-2 text-center"
      data-testid="save-error"
    >
      {{ saveError }}
    </p>
    <v-progress-linear v-if="saving" indeterminate color="brandCyan" aria-label="Saving" />

    <!-- Bottom action buttons -->
    <div class="d-flex justify-end pa-4">
      <Button
        :dataTestId="'cancel-btn'"
        text
        color="brandPrimary"
        class="mr-4"
        @click="handleCancel"
        >Cancel</Button
      >
      <Button
        :dataTestId="'save-btn'"
        color="brandPrimary"
        :loading="saving"
        :disabled="saving || uploading"
        @click="onSave"
        >Save</Button
      >
    </div>

    <!-- Unsaved changes modal -->
    <UnsavedChangesDialog v-model="showUnsavedModal" @stay="handleStay" @discard="handleDiscard" />
  </div>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import DragAndDropImageInput from "@/components/DragAndDropImageInput.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import Billboard from "@/components/program/participant/dashboard/Billboard.vue";
import ProgramService from "@/services/ProgramService";
import AudienceGroupSelector from "@/components/account-management/dashboard/AudienceGroupSelector.vue";
import { isValidImageUrl } from "@/utils/isValidImageUrl";
import { mapState } from "vuex";

function initialState() {
  return {
    isDirty: false,
    showUnsavedModal: false,
    saving: false,
    saveError: null,
    backgroundMode: "color",
    showGradient: false,
    aspectRatio: "4:1",
    primaryColor: "#80FFEE",
    secondaryColor: "#80FFEE",
    imageUrl: null,
    uploading: false,
    uploadError: null,
    // #190172 Item C: a newly selected image whose inline upload failed is a
    // pending intent that must block the next save (the old image is still on
    // the column only because the new upload didn't land).
    pendingImageFailed: false,
    titleEnabled: true,
    title: null,
    titleColor: "#000000",
    titleBold: false,
    titleItalic: false,
    titleUnderline: false,
    titleSize: "md",
    horizontalAlignment: "center",
    verticalAlignment: "center",
    imageFile: null,
    rowId: null,
    preloadedPrimaryColor: "#80FFEE",
    preloadedSecondaryColor: "#80FFEE",
    preloadedShowGradient: false,
    preloadedTitleColor: "#000000",
    gradientRecoveryReliable: true,
    preloadedGradientRaw: null,
    audienceGroups: null,
    createdColumnId: null,
  };
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Structural redesign (187347 + 187388), design doc §2.2:
// Presence-based merge -- the deliberate INVERSE of the old
// `nullCoalesceMergeConfig` contract for the present-and-null case:
//   - absent key in newConfig  -> KEEP (fall back to existingConfig's value)
//   - present key, null value  -> CLEAR (null wins, written through)
//   - present key, any value   -> SET (wins verbatim, incl. '', 0, false)
// One level deep: nested plain objects merge one level, but a doubly-nested
// object present in newConfig replaces (does not deep-merge) the existing
// nested object at that key.
export function mergeColumnConfig(existingConfig, newConfig) {
  const existing = existingConfig || {};
  const merged = { ...existing };
  for (const [key, value] of Object.entries(newConfig || {})) {
    if (isPlainObject(value) && isPlainObject(existing[key])) {
      merged[key] = { ...existing[key], ...value };
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

// Structural redesign (187347 + 187388), design doc §2.3/§4 R1:
// The single serializer on the PERSISTENCE path (buildConfig -> onSave ->
// mergeColumnConfig -> ship): the ONLY code permitted to add/omit
// color/gradient/secondaryColor on the `background` object that gets
// persisted. KEEP omits all three keys entirely; CLEAR sets all three to
// exactly `null`; SET writes the concrete/`null` sub-values from the
// directive verbatim. Centralizing this here prevents any other code path
// on the persistence path from re-creating the omit-vs-null ambiguity that
// caused the original defect. Note: the preview-only `Config` computed
// property constructs a background object independently for local preview
// rendering only -- it is NOT part of the persisted output and does not
// go through this function.
export function applyColorGroupDirective(background, directive) {
  if (directive.action === "keep") {
    delete background.color;
    delete background.gradient;
    delete background.secondaryColor;
  } else if (directive.action === "clear") {
    background.color = null;
    background.gradient = null;
    background.secondaryColor = null;
  } else if (directive.action === "set") {
    background.color = directive.color;
    background.gradient = directive.gradient ?? null;
    background.secondaryColor = directive.secondaryColor ?? null;
  }
  return background;
}

function truthy(value) {
  return value !== null && value !== undefined && value !== "";
}

function buildGradientString(primaryColor, secondaryColor) {
  return `linear-gradient(45deg,${primaryColor} 1%,${secondaryColor} 100%)`;
}

// Structural redesign (187347 + 187388), design doc §2.3:
// Builds a SET directive from raw/live UI state. `color`/`gradient`/
// `secondaryColor` always move together as one atomic unit (187388's
// insight): if a gradient isn't actively shown (or has no usable secondary
// stop), gradient/secondaryColor are explicit CLEAR sub-values within the
// SET directive -- never a stale/resurrected value.
function setFromRaw(ctx) {
  const color = ctx.primaryColor;
  let gradient = null;
  let secondaryColor = null;
  if (ctx.showGradient && truthy(ctx.secondaryColor)) {
    const userRepickedStops = !!ctx.primaryTouched || !!ctx.secondaryTouched;
    gradient =
      ctx.gradientRecoveryReliable || userRepickedStops
        ? buildGradientString(color, ctx.secondaryColor)
        : ctx.preloadedGradientRaw;
    secondaryColor = ctx.secondaryColor;
  }
  return { action: "set", color, gradient, secondaryColor };
}

// Structural redesign (187347 + 187388), design doc §2.3/§6.B:
// Pure resolver deciding the color group's KEEP/CLEAR/SET intent. Branches
// A-D exactly match the design doc's pseudocode -- see the design doc for
// the full rationale behind each branch.
export function resolveColorGroup(ctx) {
  if (ctx.currentMode !== "color") {
    return { action: "clear" }; // (A) not in color mode: group must not persist
  }

  const enteringFromConcreteDifferentMode =
    ctx.previousMode != null && ctx.previousMode !== "color";
  const legacyAbsentMode = ctx.previousMode == null;

  if (ctx.isNewColumn || enteringFromConcreteDifferentMode) {
    return setFromRaw(ctx); // (B) no trustworthy existing group to fall back to
  }

  if (legacyAbsentMode) {
    // (C) legitimate stored group; preserve unless the user edited it.
    return ctx.colorGroupTouched ? setFromRaw(ctx) : { action: "keep" };
  }

  // (D) previousMode === "color": ordinary same-mode resave
  return ctx.colorGroupTouched ? setFromRaw(ctx) : { action: "keep" };
}

export default {
  name: "BillboardCreator",
  components: {
    DrawerToolbar,
    Button,
    Icon,
    DragAndDropImageInput,
    UnsavedChangesDialog,
    Billboard,
    AudienceGroupSelector,
  },
  props: {
    row: { type: Object, default: () => ({}) },
    type: { type: String, default: "billboard" },
    widgetTypes: { type: Array, required: true },
    rowCount: { type: Number, default: 0 },
    showBackButton: { type: Boolean, default: true },
    clientId: { type: [Number, String], default: null },
    globalDefaults: { type: Object, default: () => ({}) },
  },
  data() {
    return { ...initialState(), preloading: false };
  },
  computed: {
    ...mapState(["globalClientId"]),
    aspectRatioValue() {
      const parts = (this.aspectRatio || "4:1").split(":");
      return Number(parts[0]) / Number(parts[1]);
    },
    aspectRatioItems() {
      return [
        { text: "Large, 4:1 aspect ratio", value: "4:1" },
        { text: "Medium, 10:1 aspect ratio", value: "10:1" },
      ];
    },
    verticalAlignmentItems() {
      return [
        { text: "Top", value: "start" },
        { text: "Center", value: "center" },
        { text: "Bottom", value: "end" },
      ];
    },
    titleSizeItems() {
      return [
        { text: "Small", value: "sm" },
        { text: "Medium", value: "md" },
        { text: "Large", value: "lg" },
      ];
    },
    Config() {
      let config = {
        aspectRatio: this.aspectRatio,
      };
      if (this.backgroundMode === "image" && this.imageUrl) {
        config.image = { url: this.imageUrl };
      } else if (this.backgroundMode === "color") {
        if (this.showGradient && this.secondaryColor) {
          config.background = {
            gradient: `linear-gradient(45deg,${this.primaryColor} 1%,${this.secondaryColor} 100%)`,
          };
        } else {
          config.background = { color: this.primaryColor };
        }
      }
      if (this.titleEnabled && this.title) {
        config.overlay = {
          text: this.title,
          color: this.titleColor,
          bold: this.titleBold,
          italic: this.titleItalic,
          underline: this.titleUnderline,
          horizontalAlignment: this.horizontalAlignment,
          verticalAlignment: this.verticalAlignment,
          size: this.titleSize,
        };
      }
      return config;
    },
  },
  created() {
    this.preloading = true;
    this.preloadRow();
    this.$nextTick(() => {
      this.preloading = false;
      this.isDirty = false;
    });
  },
  methods: {
    snapshotPreloadedStyles() {
      this.preloadedPrimaryColor = this.primaryColor;
      this.preloadedSecondaryColor = this.secondaryColor;
      this.preloadedShowGradient = this.showGradient;
      this.preloadedTitleColor = this.titleColor;
    },
    preloadRow() {
      // Reset to defaults first
      Object.assign(this.$data, { ...initialState(), preloading: this.preloading });
      this.audienceGroups = this.row?.audienceGroups ?? null;

      const column = this.row?.DashboardColumns?.[0];
      if (!column) {
        // New component — apply global defaults
        if (this.globalDefaults.cardBackgroundColor) {
          this.primaryColor = this.globalDefaults.cardBackgroundColor;
        }
        if (this.globalDefaults.textColor) {
          this.titleColor = this.globalDefaults.textColor;
        }
        this.snapshotPreloadedStyles(); // G1: baseline snapshot AFTER load, end of preloadRow()
        return;
      }
      const config = column.config;
      if (!config) {
        this.snapshotPreloadedStyles(); // G1
        return;
      }

      if (config.aspectRatio) {
        this.aspectRatio = config.aspectRatio;
      } else if (this.type === "headline") {
        this.aspectRatio = "10:1";
      }

      // Structural redesign (187347 + 187388), design doc §2.5/Q2: gate the
      // color-group load on the *effective* mode, not the raw stored mode.
      // absent mode + stored image.url => effective image; absent mode with
      // no image => effective color (legacy); otherwise the stored mode.
      const storedMode = config.background?.mode;
      const hasStoredImageUrl = !!config.image?.url;
      let effectiveMode;
      if (storedMode === "image") {
        effectiveMode = "image";
      } else if (storedMode == null && hasStoredImageUrl) {
        effectiveMode = "image";
      } else if (storedMode == null) {
        effectiveMode = "color";
      } else {
        effectiveMode = storedMode;
      }
      this.backgroundMode = effectiveMode;

      // Local, synchronous captures -- fed into `this.*` at the end of this
      // block so the reliability baseline is derived directly from the
      // loaded config payload (G1/residual-risk #1), never from reactive
      // state that could be mutated later in the same tick.
      let gradientRecoveryReliable = true;
      let preloadedGradientRaw = null;

      if (effectiveMode === "color" && config.background) {
        if (config.background.color) {
          this.primaryColor = config.background.color;
        }
        if (config.background.gradient) {
          this.showGradient = true;
          preloadedGradientRaw = config.background.gradient;
          // Salvaged from 187388: regex-recover hex stops from a legacy
          // gradient string that may embed non-hex (e.g. rgb()) stops.
          const hexStops = config.background.gradient.match(/#[0-9A-Fa-f]{6}/g) || [];
          if (hexStops[0] && !config.background.color) {
            this.primaryColor = hexStops[0];
          }
          if (hexStops[1]) {
            this.secondaryColor = hexStops[1];
          }
          gradientRecoveryReliable = hexStops.length >= 2;
          if (!gradientRecoveryReliable) {
            // eslint-disable-next-line no-console
            console.warn(
              "BillboardCreator: could not fully recover gradient stops from the stored gradient string; some colors may be approximate."
            );
          }
        }
        if (config.background.secondaryColor) {
          this.secondaryColor = config.background.secondaryColor;
        }
      }

      this.gradientRecoveryReliable = gradientRecoveryReliable;
      this.preloadedGradientRaw = preloadedGradientRaw;

      if (config.image && config.image.url) {
        this.imageUrl = config.image.url;
      }

      const titleSource = config.title || config.overlay || null;
      if (titleSource) {
        const text = config.title ? config.title.text : config.overlay.text;
        if (text !== undefined) this.title = text;

        const color = titleSource.color;
        if (color !== undefined) this.titleColor = color;

        if (titleSource.bold !== undefined) this.titleBold = titleSource.bold;
        if (titleSource.italic !== undefined) this.titleItalic = titleSource.italic;
        if (titleSource.underline !== undefined) this.titleUnderline = titleSource.underline;
        if (titleSource.size !== undefined) this.titleSize = titleSource.size;
        if (titleSource.horizontalAlignment !== undefined) {
          this.horizontalAlignment = titleSource.horizontalAlignment;
        }
        if (titleSource.verticalAlignment !== undefined) {
          this.verticalAlignment = titleSource.verticalAlignment || "center";
        }
        if (titleSource.enabled !== undefined) {
          this.titleEnabled = titleSource.enabled;
        }
      }

      // G1: single snapshot call, at the END of preloadRow(), after all
      // color-group loads (incl. regex-recovered hex stops) have completed.
      this.snapshotPreloadedStyles();
    },
    // Structural redesign (187347 + 187388), design doc §2.4/Q3: THE single
    // source of truth for every touched flag. Nothing else in this
    // component may (re)compute a touched flag -- `buildConfig()` and
    // `onSave()` both consume this method's return value only (G2).
    // NOTE: this state is a pure, synchronous, per-field value diff. It is
    // NEVER persisted -- it is not written to Vuex/pinia, not included in
    // any API payload, and not part of the config object shipped to the
    // backend (see `buildConfig`/`onSave` below).
    computeStyleTouchState() {
      const norm = (value) => (typeof value === "string" ? value.toLowerCase() : value);
      const primaryTouched = norm(this.primaryColor) !== norm(this.preloadedPrimaryColor);
      const secondaryTouched = norm(this.secondaryColor) !== norm(this.preloadedSecondaryColor);
      const gradientToggleTouched = this.showGradient !== this.preloadedShowGradient;
      const titleColorTouched = norm(this.titleColor) !== norm(this.preloadedTitleColor);
      const colorGroupTouched = primaryTouched || secondaryTouched || gradientToggleTouched;
      return {
        primaryTouched,
        secondaryTouched,
        gradientToggleTouched,
        titleColorTouched,
        colorGroupTouched,
      };
    },

    handleBack() {
      if (this.isDirty) {
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleCancel() {
      if (this.isDirty) {
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleDiscard() {
      this.showUnsavedModal = false;
      this.$emit("close");
    },
    handleStay() {
      this.showUnsavedModal = false;
    },
    addGradient() {
      this.showGradient = true;
      this.markDirty();
    },
    removeGradient() {
      this.showGradient = false;
      this.secondaryColor = "#80FFEE";
      this.markDirty();
    },
    async onFileSelected(file) {
      if (!file) return;
      if (this.uploading) return;
      this.pendingImageFailed = false;

      const columnId =
        this.row?.DashboardColumns?.[0]?.columnId ??
        this.row?.DashboardColumns?.[0]?.dashboardColumnId ??
        this.createdColumnId;

      if (columnId) {
        this.uploading = true;
        this.uploadError = null;
        try {
          const response = await ProgramService.uploadDashboardColumnImage(columnId, file);
          const imageUrl = response?.data?.result;
          if (!isValidImageUrl(imageUrl)) {
            throw new Error("Upload failed");
          }
          this.imageUrl = imageUrl;
          this.markDirty();
        } catch (e) {
          this.uploadError = e.message || "Upload failed";
          this.pendingImageFailed = true;
        } finally {
          this.uploading = false;
        }
      } else {
        this.imageFile = file;
        this.imageUrl = URL.createObjectURL(file);
        this.markDirty();
      }
    },
    onClearImage() {
      this.imageUrl = null;
      this.imageFile = null;
      this.uploadError = null;
      this.pendingImageFailed = false;
      this.markDirty();
    },
    // Structural redesign (187347 + 187388), design doc §2.5: build -> merge
    // -> ship, with NO post-hoc mode-transition branches. `touch` is
    // computed exactly once by `onSave()` (G2) and threaded through here;
    // `buildConfig` does NOT recompute any touched flag.
    async onSave() {
      if (Array.isArray(this.audienceGroups) && this.audienceGroups.length === 0) {
        return;
      }
      if (this.uploading) return;
      this.saving = true;
      this.saveError = null;
      // #190179 Item C: attempt-scoped — clear any stale error from a prior attempt.
      this.uploadError = null;
      // #190172 Item C: a newly selected image whose inline upload failed must
      // block the save (gate ONLY on the pending-failure flag, not uploadError,
      // so a genuinely stale error from a prior attempt still allows saving).
      // Only fires in image mode -- in color mode buildConfig persists no image.
      if (this.pendingImageFailed && this.backgroundMode === "image") {
        this.saveError = "Your new image didn't upload. Please re-select and try again.";
        this.uploadError = "Upload failed";
        this.saving = false;
        return;
      }
      try {
        const touch = this.computeStyleTouchState(); // G2: computed exactly once
        const config = this.buildConfig(touch);

        let rowId = this.row?.rowId || this.rowId;
        if (!rowId) {
          const newRow = await ProgramService.createDashboardRow({
            rowTypeId: this.row.rowTypeId,
            programId: this.row.programId,
            position: this.rowCount + 1,
            clientId: this.clientId || this.globalClientId,
          });
          rowId = newRow.data.result.rowId;
          this.rowId = rowId;
        }

        let columnId =
          this.row?.DashboardColumns?.[0]?.columnId ??
          this.row?.DashboardColumns?.[0]?.dashboardColumnId ??
          this.createdColumnId;
        const isNewColumn = !columnId;
        const widgetTypeId = this.widgetTypes?.find((wt) => wt.type === this.type)?.widgetTypeId;

        // `mergeColumnConfig` merges by PRESENCE, not nullness (§2.2): any
        // leaf `buildConfig` omitted (KEEP) falls back to the existing
        // stored value; any leaf present-and-null (CLEAR) writes through.
        // No existing config to merge against on a brand-new column.
        const existingConfig = this.row?.DashboardColumns?.[0]?.config || {};
        const mergedConfig = isNewColumn ? config : mergeColumnConfig(existingConfig, config);

        // `image.url` and `overlay.text` are direct reflections of current
        // UI state (not touched-gated) and must always win outright,
        // bypassing the merge (unchanged from the prior contract).
        mergedConfig.image = config.image;
        if (mergedConfig.overlay) mergedConfig.overlay.text = config.overlay.text;

        const body = {
          rowId,
          widgetTypeId,
          config: mergedConfig,
          position: 1,
          clientId: this.clientId || this.globalClientId,
        };

        if (columnId) body.columnId = columnId;

        if (this.imageFile) body.config.image = undefined;

        if (isNewColumn) {
          const newColumn = await ProgramService.createDashboardColumn(body);
          columnId = newColumn.data.result.columnId;
          this.createdColumnId = columnId;
        } else {
          await ProgramService.updateDashboardColumn(columnId, body);
        }

        if (this.imageFile) {
          const uploadResponse = await ProgramService.uploadDashboardColumnImage(
            columnId,
            this.imageFile
          );
          const imageUrl = uploadResponse?.data?.result;
          if (!isValidImageUrl(imageUrl)) {
            throw new Error("Upload failed");
          }
          this.imageUrl = imageUrl;
          const finalBody = {
            ...body,
            config: { ...body.config, image: { url: imageUrl } },
            columnId,
          };
          await ProgramService.updateDashboardColumn(columnId, finalBody);
        }

        if (!this.row?.programId && rowId) {
          await ProgramService.updateDashboardRow(rowId, {
            audienceGroups: this.audienceGroups,
          });
        }
        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        this.saveError = e.message || "Save failed";
      } finally {
        this.saving = false;
      }
    },

    // Structural redesign (187347 + 187388), design doc §2.5: consumes the
    // single `touch` state computed by `computeStyleTouchState()` -- it
    // does NOT recompute any touched flag (G2). Defaults `touch` for
    // standalone callers (e.g. direct unit invocation) but production call
    // sites always pass the result of `computeStyleTouchState()` explicitly.
    buildConfig(touch = this.computeStyleTouchState()) {
      const column = this.row?.DashboardColumns?.[0];
      const existingConfig = column?.config || {};
      const isNewColumn = !(column?.columnId ?? column?.dashboardColumnId);
      const previousMode = existingConfig?.background?.mode;

      const directive = resolveColorGroup({
        currentMode: this.backgroundMode,
        previousMode,
        isNewColumn,
        colorGroupTouched: touch.colorGroupTouched,
        primaryColor: this.primaryColor,
        showGradient: this.showGradient,
        secondaryColor: this.secondaryColor,
        preloadedGradientRaw: this.preloadedGradientRaw,
        gradientRecoveryReliable: this.gradientRecoveryReliable,
        primaryTouched: touch.primaryTouched,
        secondaryTouched: touch.secondaryTouched,
      });

      const background = { mode: this.backgroundMode };
      applyColorGroupDirective(background, directive);

      // SET-only reshape (187347/187388): the backend's
      // `mergeBackgroundStyle()` (data-access-layer-program) treats
      // `background.color`/`background.gradient` as mutually exclusive and,
      // when both are non-null, lets `color` win. That's fine for KEEP --
      // this directive didn't touch the color group, so we must not
      // reinterpret whatever a legacy row already has stored (which one the
      // admin "meant" is ambiguous, and ADO 187388's acceptance criterion is
      // to preserve background/color untouched unless intentionally
      // changed). But for a fresh SET, this save's own gradient must win
      // outright, so `color` is forced to explicit null here. Gated
      // explicitly on `directive.action === "set"` even though KEEP already
      // omits these keys and CLEAR already nulls gradient -- the gate
      // documents intent and guards against future changes to
      // `applyColorGroupDirective`'s branches.
      if (directive.action === "set" && background.gradient != null) {
        background.color = null;
      }

      const overlay = {
        text: this.title,
        bold: this.titleBold,
        italic: this.titleItalic,
        underline: this.titleUnderline,
        size: this.titleSize,
        horizontalAlignment: this.horizontalAlignment,
        verticalAlignment: this.verticalAlignment,
        enabled: this.titleEnabled,
      };
      // R6: overlay.color follows the same omit-based KEEP semantics as the
      // color group -- emit the key only when titleColor was touched.
      if (touch.titleColorTouched) {
        overlay.color = this.titleColor;
      }

      return {
        aspectRatio: this.aspectRatio,
        background,
        image: {
          url: this.backgroundMode === "image" ? this.imageUrl : null,
        },
        overlay,
      };
    },
    markDirty() {
      if (!this.preloading) this.isDirty = true;
    },
  },
  watch: {
    row: {
      handler() {
        this.preloading = true;
        this.preloadRow();
        this.$nextTick(() => {
          this.preloading = false;
          this.isDirty = false;
        });
      },
    },
    aspectRatio() {
      this.markDirty();
    },
    backgroundMode() {
      this.markDirty();
    },
    primaryColor() {
      this.markDirty();
    },
    secondaryColor() {
      this.markDirty();
    },
    imageUrl() {
      this.markDirty();
    },
    titleEnabled() {
      this.markDirty();
    },
    title() {
      this.markDirty();
    },
    titleColor() {
      this.markDirty();
    },
    titleBold() {
      this.markDirty();
    },
    titleItalic() {
      this.markDirty();
    },
    titleUnderline() {
      this.markDirty();
    },
    titleSize() {
      this.markDirty();
    },
    horizontalAlignment() {
      this.markDirty();
    },
    verticalAlignment() {
      this.markDirty();
    },
    audienceGroups() {
      this.markDirty();
    },
  },
};
</script>

<style scoped>
.format-btn--active {
  border: 2px solid currentColor !important;
  border-radius: 4px !important;
}

.radio-option {
  cursor: pointer;
}

.color-swatch {
  width: 40px;
  height: 40px;
  min-width: 40px;
  flex-shrink: 0;
  margin-right: 8px;
  margin-bottom: 22px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
}
.color-swatch::-webkit-color-swatch-wrapper {
  padding: 0;
}
.color-swatch::-webkit-color-swatch {
  border: none;
}

.title-color-swatch {
  width: 24px;
  height: 24px;
  min-width: 24px;
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
}
.title-color-swatch::-webkit-color-swatch-wrapper {
  padding: 0;
}
.title-color-swatch::-webkit-color-swatch {
  border: none;
}

.vertical-alignment-select {
  max-width: 166px;
}
</style>
